import { useCallback, useEffect, useMemo, useState } from 'react';
import { type ExportStatus, getExportStatus } from 'api/exportStatus';
import { ResultTemplateKey } from 'types/result';
import type { VitalParameterKey } from 'widgets/results/VitalParametersSection';
import { useExportResult } from 'hooks/exportResultAsync';

type ExportedVitalParameter =
  | 'systolic_mmhg'
  | 'diastolic_mmhg'
  | 'pulse_bpm'
  | 'spo2_percent'
  | 'temperature_c'
  | 'respiratory_rate_bpm'
  | 'weight_kg'
  | 'height_cm';

const VITAL_PARAMETER_MAPPING: Partial<Record<VitalParameterKey, ExportedVitalParameter>> = {
  systolicMmhg: 'systolic_mmhg',
  diastolicMmhg: 'diastolic_mmhg',
  pulseBpm: 'pulse_bpm',
  spo2Percent: 'spo2_percent',
  temperatureC: 'temperature_c',
  respiratoryRateBpm: 'respiratory_rate_bpm',
  weightKg: 'weight_kg',
  heightCm: 'height_cm',
};

export interface UseDataExportReturn {
  exportStatuses: ExportStatus[];
  successfullyExportedFields: Set<string>;
  handleDataExportAsync: (options?: { retryFilter?: Set<string> }) => Promise<{ success: boolean }>;
}

interface UseDataExportProps {
  resultId: string | number;
  anamnesisId: string | undefined;
  checkedSections: Set<ResultTemplateKey | string>;
  vitalParameterSelectionMap: Record<VitalParameterKey, boolean>;
}

export const useDataExport = ({
  resultId,
  anamnesisId,
  checkedSections,
  vitalParameterSelectionMap,
}: UseDataExportProps): UseDataExportReturn => {
  const [exportStatuses, setExportStatuses] = useState<ExportStatus[]>([]);

  const successfullyExportedFields = useMemo(() => {
    const latestByField = new Map<string, ExportStatus>();
    exportStatuses.forEach((s) => {
      if (!s.fieldName) return;
      const existing = latestByField.get(s.fieldName);
      if (!existing || s.createdAt > existing.createdAt) {
        latestByField.set(s.fieldName, s);
      }
    });
    return new Set([...latestByField.values()].filter((s) => s.success).map((s) => s.fieldName!));
  }, [exportStatuses]);

  const { mutateAsync } = useExportResult();

  const fetchExportStatuses = useCallback(async () => {
    if (!resultId) return;
    try {
      const promises = [getExportStatus(String(resultId))];
      if (anamnesisId) {
        promises.push(getExportStatus(String(anamnesisId)));
      }
      const results = await Promise.all(promises);
      const statuses = results.flat();
      setExportStatuses(Array.isArray(statuses) ? statuses : []);
    } catch (error) {
      console.error('Failed to fetch export statuses', error);
    }
  }, [resultId, anamnesisId]);

  useEffect(() => {
    void fetchExportStatuses();
  }, [fetchExportStatuses]);

  const handleDataExportAsync = useCallback(
    async (options?: { retryFilter?: Set<string> }) => {
      const { retryFilter } = options ?? {};
      const include = (key: string) => !retryFilter || retryFilter.has(key);

      const sectionsToBeExported: Record<string, true | string[]> = {};

      const simpleSections = [
        ResultTemplateKey.Anamnesis,
        ResultTemplateKey.Findings,
        ResultTemplateKey.Therapy,
        ResultTemplateKey.Procedure,
        ResultTemplateKey.Comment,
      ];

      simpleSections.forEach((key) => {
        if (checkedSections.has(key) && include(key)) sectionsToBeExported[key] = true;
      });

      const isSelfAnamnesisExported = exportStatuses.some(
        (s) => s.fieldName === 'selfAnamnesis' && s.success
      );

      if (!isSelfAnamnesisExported && anamnesisId) {
        sectionsToBeExported[ResultTemplateKey.SelfAnamnesis] = true;
      }

      if (
        checkedSections.has(ResultTemplateKey.Diagnosis) &&
        include(ResultTemplateKey.DiagnosedIcd)
      ) {
        sectionsToBeExported[ResultTemplateKey.DiagnosedIcd] = true;
      }

      if (checkedSections.has(ResultTemplateKey.EbmCodes) && include(ResultTemplateKey.EbmCodes)) {
        sectionsToBeExported[ResultTemplateKey.EbmCodes] = true;
      }

      if (
        checkedSections.has(ResultTemplateKey.Immunization) &&
        include(ResultTemplateKey.Immunization)
      ) {
        sectionsToBeExported[ResultTemplateKey.Immunization] = true;
      }

      const selectedVitalKeys = (Object.keys(vitalParameterSelectionMap) as VitalParameterKey[])
        .filter((key) => vitalParameterSelectionMap[key] && VITAL_PARAMETER_MAPPING[key])
        .map((key) => VITAL_PARAMETER_MAPPING[key]!);

      if (selectedVitalKeys.length > 0 && include(ResultTemplateKey.VitalParameters)) {
        sectionsToBeExported[ResultTemplateKey.VitalParameters] = selectedVitalKeys;
      }

      return mutateAsync({ resultId, sections: sectionsToBeExported });
    },
    [
      exportStatuses,
      anamnesisId,
      checkedSections,
      vitalParameterSelectionMap,
      mutateAsync,
      resultId,
    ]
  );

  return {
    exportStatuses,
    successfullyExportedFields,
    handleDataExportAsync,
  };
};
